import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import type * as React from 'react'

import { cn } from '@/shared/helpers/styles.helper'
import { MoreHorizontalIcon } from 'lucide-react'

// Fil d'Ariane : navigation entre la page courante et ses pages parentes
function Breadcrumb({ className, ...props }: React.ComponentProps<'nav'>) {
  return <nav aria-label="breadcrumb" data-slot="breadcrumb" className={cn(className)} {...props} />
}

// Liste ordonnée des maillons du fil d'Ariane
function BreadcrumbList({ className, ...props }: React.ComponentProps<'ol'>) {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={cn('text-p2 flex flex-wrap items-center wrap-break-word', className)}
      {...props}
    />
  )
}

// Maillon du fil d'Ariane
function BreadcrumbItem({ className, ...props }: React.ComponentProps<'li'>) {
  return <li data-slot="breadcrumb-item" className={cn('gap-1.5 inline-flex items-center', className)} {...props} />
}

// Exporté à part : `BreadcrumbLink` passe par `useRender`, un hook, donc il ne peut pas être rendu
// par un composant serveur. Un fil d'Ariane rendu sur le serveur habille son propre lien avec cette
// classe plutôt que de redéclarer le style.
export const BREADCRUMB_LINK_CLASS =
  'text-primary font-semibold outline-ring/50 transition-colors hover:text-[var(--blue-700)] dark:hover:text-[var(--blue-300)]'

// Lien vers une page parente. Le projet passe son composant de lien par `render` ;
// sans `render`, le lien est une balise `a`.
function BreadcrumbLink({ className, render, ...props }: useRender.ComponentProps<'a'>) {
  return useRender({
    defaultTagName: 'a',
    props: mergeProps<'a'>({ className: cn(BREADCRUMB_LINK_CLASS, className) }, props),
    render,
    state: {
      slot: 'breadcrumb-link'
    }
  })
}

const NON_LINK_ITEM_CLASS = 'text-foreground font-normal'

// Page courante, dernier maillon du fil d'Ariane
function BreadcrumbPage({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    // biome-ignore lint/a11y/useFocusableInteractive: shadcn pattern, BreadcrumbPage is a non-interactive current-page indicator
    <span
      data-slot="breadcrumb-page"
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn(NON_LINK_ITEM_CLASS, className)}
      {...props}
    />
  )
}

// Maillon intermédiaire sans page à ouvrir. Pas d'`aria-current` : la page courante reste le seul
// BreadcrumbPage du fil.
function BreadcrumbText({ className, ...props }: React.ComponentProps<'span'>) {
  return <span data-slot="breadcrumb-text" className={cn(NON_LINK_ITEM_CLASS, className)} {...props} />
}

// Séparateur entre deux maillons, une barre oblique par défaut
function BreadcrumbSeparator({ children, className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={cn('px-breadcrumb [&>svg]:size-3.5', className)}
      {...props}
    >
      {children ?? <span>/</span>}
    </li>
  )
}

// Points de suspension à la place des maillons masqués d'un fil d'Ariane long
function BreadcrumbEllipsis({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      aria-hidden="true"
      className={cn('size-5 [&>svg]:size-4 flex items-center justify-center', className)}
      {...props}
    >
      <MoreHorizontalIcon />
      <span className="sr-only">More</span>
    </span>
  )
}

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbText,
  BreadcrumbSeparator,
  BreadcrumbEllipsis
}
